// Central client for the per-project £2.99 unlock entitlement.
//
// Commercial rule: creating, measuring and editing a project is always free.
// A clean, watermark-free PDF/PNG/JPG/SVG/DXF export of a project requires a
// server-verified unlock. The entitlement is keyed ONLY to the project's
// existing `project.id` UUID — never a client-side boolean — so every check
// here goes to the backend, which is the sole source of truth.
//
// Exactly what leaves the browser: the job identity text the user already
// typed for their title block (address/title), the project's own opaque
// floor UUIDs, and small integer counts (floor/room/wall counts). Never
// geometry, coordinates, images, PDFs or measurement values.
import axios from "axios";
import { API_URL } from "@/utils/api";

export const PROJECT_UNLOCK_PRICE_GBP = 2.99;

// ---- Admin unlimited access (session-only, server-verified) ----
// Deliberately NOT a client-side `isAdmin=true` flag: the actual permission
// is decided server-side on every check-export call by re-validating this
// same password against ADMIN_PASSWORD (identical mechanism already used by
// the Admin Dashboard's revoke/list/summary calls). sessionStorage only
// caches the already-verified password for this browser tab so the admin
// isn't re-prompted on every export; clearing it (sign-out, or simply
// closing the tab) fully revokes the client's ability to even ATTEMPT the
// bypass — the server would reject a stale/incorrect value regardless.
const ADMIN_PW_KEY = "hpd_admin_password";
const ADMIN_MODE_KEY = "hpd_admin_mode_enabled";

export function getCachedAdminPassword() {
  try { return sessionStorage.getItem(ADMIN_PW_KEY) || ""; } catch { return ""; }
}

export function isAdminModeEnabled() {
  return !!getCachedAdminPassword() && (() => { try { return sessionStorage.getItem(ADMIN_MODE_KEY) !== "false"; } catch { return true; } })();
}

export async function verifyAndEnableAdminAccess(password) {
  await axios.post(`${API_URL}/project-unlock/admin/verify`, {}, { headers: { "x-admin-password": password } });
  try {
    sessionStorage.setItem(ADMIN_PW_KEY, password);
    sessionStorage.setItem(ADMIN_MODE_KEY, "true");
  } catch { /* sessionStorage unavailable — admin mode simply won't persist */ }
}

// "Preview locked-user experience": keeps the verified password cached (no
// need to re-enter it) but stops sending it, so the admin sees exactly what
// a normal customer sees — without ever touching the project's own
// entitlement state.
export function setAdminModeEnabled(enabled) {
  try { sessionStorage.setItem(ADMIN_MODE_KEY, enabled ? "true" : "false"); } catch { /* noop */ }
}

export function signOutOfAdminAccess() {
  try { sessionStorage.removeItem(ADMIN_PW_KEY); sessionStorage.removeItem(ADMIN_MODE_KEY); } catch { /* noop */ }
}


export function getJobIdentity(project) {
  const addr = (project?.propertyAddress || "").trim();
  if (addr) return addr;
  const title = (project?.title || project?.name || "").trim();
  if (title) return title;
  return "Untitled project";
}

export function computeProjectSignals(project) {
  const floors = project?.floorPlans || [];
  return {
    job_identity: getJobIdentity(project),
    floor_ids: floors.map((f) => f.id).filter(Boolean),
    floor_count: floors.length,
    room_count: floors.reduce((n, f) => n + (f.rooms?.length || 0), 0),
    wall_count: floors.reduce((n, f) => n + (f.walls?.length || 0), 0),
  };
}

export async function fetchEntitlementStatus(projectId) {
  if (!projectId) return { unlocked: false };
  try {
    const { data } = await axios.get(`${API_URL}/project-unlock/status/${projectId}`);
    return data;
  } catch {
    // Fail closed: a network hiccup must never present as "unlocked".
    return { unlocked: false, error: true };
  }
}

export async function unlockProject(projectId, paypalOrderId, project) {
  const { data } = await axios.post(`${API_URL}/project-unlock/unlock`, {
    project_id: projectId,
    paypal_order_id: paypalOrderId,
    signals: computeProjectSignals(project),
  });
  return data;
}

// Must be called immediately before generating ANY clean/final export
// (PDF, PNG, JPG, SVG, DXF, print). Only returns allowed:true when the
// backend's authoritative entitlement check passes.
export async function requestCleanExport(project) {
  if (!project?.id) return { allowed: false, reason: "no_project" };
  try {
    const headers = {};
    if (isAdminModeEnabled()) headers["x-admin-password"] = getCachedAdminPassword();
    const { data } = await axios.post(
      `${API_URL}/project-unlock/check-export/${project.id}`,
      { signals: computeProjectSignals(project) },
      { headers }
    );
    return data;
  } catch {
    return { allowed: false, reason: "network_error" };
  }
}

// Redeems a complimentary-access invite code/token for the CURRENT project.
// Server-validated only — the token itself carries the entire permission,
// never anything read from localStorage/sessionStorage.
export async function redeemInvite(token, project) {
  const { data } = await axios.post(`${API_URL}/project-unlock/redeem-invite`, {
    token: (token || "").trim(),
    project_id: project?.id,
    signals: computeProjectSignals(project),
  });
  return data;
}

